import React from "react";
import { useNavigate } from "react-router-dom";
import {
  getProjectId,
  getProjectName,
  getProjectSourcesCount,
} from "../utils/projectUtils";

/**
 * Project list / new-project flow / delete confirmation state and handlers for the header sidebar.
 */
export default function useHeaderProjects({
  projects,
  archivedProjects = [],
  activeProjectId,
  handleProjectClick,
  handleDeleteProject,
  updateProjectIsArchived,
  createProject,
  loading,
  isGenerating,
  refreshProjectFiles,
}) {
  const navigate = useNavigate();

  const allProjects = React.useMemo(
    () => [...projects, ...archivedProjects],
    [projects, archivedProjects],
  );

  const activeProject = allProjects.find(
    (project) => String(getProjectId(project)) === String(activeProjectId),
  );
  const [projectSearch, setProjectSearch] = React.useState("");
  const [isProjectSearchOpen, setIsProjectSearchOpen] = React.useState(false);
  const [deleteAnchorEl, setDeleteAnchorEl] = React.useState(null);
  const [pendingDeleteProject, setPendingDeleteProject] = React.useState(null);
  const [deletingProjectIds, setDeletingProjectIds] = React.useState({});
  const [isNewProjectInputOpen, setIsNewProjectInputOpen] = React.useState(false);
  const [newProjectDraftTitle, setNewProjectDraftTitle] = React.useState("");
  const [projectSettingsOpen, setProjectSettingsOpen] = React.useState(false);
  const [projectSettingsScope, setProjectSettingsScope] = React.useState(
    "active",
  );
  const [projectSettingsProject, setProjectSettingsProject] = React.useState(null);
  const [isProjectDetailsOpen, setIsProjectDetailsOpen] = React.useState(false);
  const [isInlineCreatingProject, setIsInlineCreatingProject] = React.useState(false);

  const filteredProjects = React.useMemo(() => {
    const query = projectSearch.trim().toLowerCase();
    const list = query
      ? projects.filter((project) =>
          getProjectName(project).toLowerCase().includes(query),
        )
      : projects;

    if (!activeProjectId) return list;

    const activeIndex = list.findIndex(
      (project) => String(getProjectId(project)) === String(activeProjectId),
    );
    if (activeIndex <= 0) return list;

    const active = list[activeIndex];
    return [
      active,
      ...list.slice(0, activeIndex),
      ...list.slice(activeIndex + 1),
    ];
  }, [projects, projectSearch, activeProjectId]);

  const filteredArchivedProjects = React.useMemo(() => {
    const query = projectSearch.trim().toLowerCase();
    const list = query
      ? archivedProjects.filter((project) =>
          getProjectName(project).toLowerCase().includes(query),
        )
      : archivedProjects;

    if (!activeProjectId) return list;

    const activeIndex = list.findIndex(
      (project) => String(getProjectId(project)) === String(activeProjectId),
    );
    if (activeIndex <= 0) return list;

    const active = list[activeIndex];
    return [
      active,
      ...list.slice(0, activeIndex),
      ...list.slice(activeIndex + 1),
    ];
  }, [archivedProjects, projectSearch, activeProjectId]);

  const projectDetailsProject = projectSettingsProject ?? activeProject;
  const activeProjectName = getProjectName(projectDetailsProject) || "New Project Title";
  const activeProjectSourcesCount = getProjectSourcesCount(projectDetailsProject);

  const closeNewProjectFlow = () => {
    setNewProjectDraftTitle("");
    setIsNewProjectInputOpen(false);
  };

  const openNewProjectPill = () => {
    setProjectSettingsProject(null);
    setIsProjectDetailsOpen(false);
    setIsNewProjectInputOpen(true);
    if (activeProjectId) {
      navigate("/", { replace: true });
    }
  };

  const handleProjectsLabelClick = () => {
    closeNewProjectFlow();
    setProjectSettingsProject(null);
    setIsProjectDetailsOpen(false);
    handleProjectClick(null);
  };

  const handleInlineProjectInputChange = (event) => {
    setNewProjectDraftTitle(event.target.value);
  };

  const submitInlineProject = async () => {
    const projectName = newProjectDraftTitle.trim();
    if (!projectName || loading || isGenerating || isInlineCreatingProject) return;
    setIsInlineCreatingProject(true);
    try {
      await createProject({ projectName });
      closeNewProjectFlow();
    } catch {
      // Error alert is handled in context.
    } finally {
      setIsInlineCreatingProject(false);
    }
  };

  const handleProjectListClick = async (project) => {
    closeNewProjectFlow();
    const clickedProjectId = getProjectId(project);
    const isAlreadyActive =
      String(clickedProjectId ?? "") === String(activeProjectId ?? "");

    setProjectSettingsProject(project ?? null);
    setIsNewProjectInputOpen(false);
    setIsProjectDetailsOpen(true);
    if (!isAlreadyActive) {
      handleProjectClick(project);
    }
    if (clickedProjectId && refreshProjectFiles) {
      try {
        const merged = await refreshProjectFiles(clickedProjectId);
        if (merged) setProjectSettingsProject(merged);
      } catch {
        // Error alert is handled in ClientContext.
      }
    }
  };

  const handleProjectDeleteClick = (event, project, anchorEl = null) => {
    event?.stopPropagation?.();
    const projectId = getProjectId(project);
    if (!projectId) return;
    setDeleteAnchorEl(anchorEl ?? event?.currentTarget ?? null);
    setPendingDeleteProject({
      id: projectId,
      title: getProjectName(project) || "Project",
    });
  };

  const closeDeletePopover = () => {
    setDeleteAnchorEl(null);
    setPendingDeleteProject(null);
  };

  const openProjectSettings = React.useCallback((scope) => {
    setProjectSettingsScope(scope === "draft" ? "draft" : "active");
    setProjectSettingsOpen(true);
  }, []);

  const openActiveProjectSettings = React.useCallback(
    async (project) => {
      const projectId = getProjectId(project);
      let projectForModal = project ?? null;
      if (projectId && refreshProjectFiles) {
        try {
          const merged = await refreshProjectFiles(projectId);
          if (merged) projectForModal = merged;
        } catch {
          // Error alert is handled in ClientContext.
        }
      }
      setProjectSettingsScope("active");
      setProjectSettingsProject(projectForModal);
      setProjectSettingsOpen(true);
    },
    [refreshProjectFiles],
  );

  const closeProjectSettings = React.useCallback(() => {
    setProjectSettingsOpen(false);
    setProjectSettingsProject(null);
  }, []);

  const handleProjectArchiveToggle = React.useCallback(
    async (project, nextIsArchived) => {
      const projectId = getProjectId(project);
      if (!projectId || !updateProjectIsArchived) return;

      await updateProjectIsArchived(projectId, nextIsArchived);
      closeProjectSettings();
      setIsProjectDetailsOpen(false);
      handleProjectClick(null);
    },
    [closeProjectSettings, handleProjectClick, updateProjectIsArchived],
  );

  const confirmDeleteProject = async () => {
    const projectId = pendingDeleteProject?.id;
    if (!projectId) return;

    const deleteKey = String(projectId);
    setDeletingProjectIds((prev) => ({ ...prev, [deleteKey]: true }));
    try {
      await handleDeleteProject(projectId);
      if (
        String(getProjectId(projectSettingsProject) ?? "") === String(projectId)
      ) {
        setProjectSettingsProject(null);
        setIsProjectDetailsOpen(false);
      }
    } catch {
      // Error alert is handled in context.
    } finally {
      setDeletingProjectIds((prev) => {
        const next = { ...prev };
        delete next[deleteKey];
        return next;
      });
      closeDeletePopover();
    }
  };

  return {
    activeProject: projectDetailsProject,
    isProjectDetailsOpen,
    filteredProjects,
    filteredArchivedProjects,
    activeProjectName,
    activeProjectSourcesCount,
    projectSearch,
    setProjectSearch,
    isProjectSearchOpen,
    setIsProjectSearchOpen,
    isNewProjectInputOpen,
    newProjectDraftTitle,
    setNewProjectDraftTitle,
    projectSettingsOpen,
    projectSettingsScope,
    openProjectSettings,
    openActiveProjectSettings,
    closeProjectSettings,
    handleProjectArchiveToggle,
    handleProjectsLabelClick,
    openNewProjectPill,
    handleInlineProjectInputChange,
    submitInlineProject,
    closeNewProjectFlow,
    handleProjectListClick,
    handleProjectDeleteClick,
    deleteAnchorEl,
    pendingDeleteProject,
    closeDeletePopover,
    confirmDeleteProject,
    deletingProjectIds,
  };
}
